import Pane from 'components/Common/Pane'
import cn from 'clsx'
import React, { MouseEventHandler, useCallback, useMemo } from 'react'
import config from 'constants/config'
import { rem } from 'polished'
import moment from 'moment'
import useIsAvailableForPurchase from '../hooks/useIsAvailableForPurchase'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import PriceRowTaxesAndFees from 'components/Luxkit/PricePoints/PriceRowTaxesAndFees'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import WalledContent from 'components/Common/WalledContent'
import OfferBookButton from '../OfferBookButtons/OfferBookButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import BusinessTravellerOfferCreditsTextLink from 'businessTraveller/components/offer-credits/BusinessTravellerOfferCreditsTextLink'
import AccommodationRoomTile from '../Accommodation/AccommodationRoomTile'
import Divider from 'components/Luxkit/Divider'
import AccommodationPackageTile from '../Accommodation/AccommodationPackageTile'
import AccommodationPackageTileContentArea from '../Accommodation/AccommodationPackageTileContentArea'
import AccommodationPackageTilePricingArea from '../Accommodation/AccommodationPackageTilePricingArea'
import AccommodationPackageTileDetails from '../Accommodation/AccommodationPackageTileDetails'
import AccommodationPackageTilePricingBlocks from '../Accommodation/AccommodationPackageTilePricingBlocks'
import AccommodationPackageTilePricingBlock from '../Accommodation/AccommodationPackageTilePricingBlock'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import AccommodationPackageTileInclusions from '../Accommodation/AccommodationPackageTileInclusions'
import AccommodationRoomTileDetails from '../Accommodation/AccommodationRoomTileDetails'
import AccommodationRoomTileImage from '../Accommodation/AccommodationRoomTileImage'
import AccommodationRoomTileActions, { AccommodationRoomTileActionsState } from '../Accommodation/AccommodationRoomTileActions'
import TextButton from 'components/Luxkit/Button/TextButton'
import { pluralizeToString } from 'lib/string/pluralize'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import PriceRowValueLastMinuteDiscount from 'components/Luxkit/PricePoints/Value/PriceRowValueLastMinuteDiscount'
import PriceRowCaption from 'components/Luxkit/PricePoints/PriceRowCaption'
import SolidFireIcon from 'components/Luxkit/Icons/solid/SolidFireIcon'
import Label from 'components/Luxkit/Label'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import InclusionsSection from '../FlashHotel/InclusionsSection'
import OfferAddToCartButton from '../OfferBookButtons/OfferAddToCartButton'
import OfferSelectPackageButton from '../OfferBookButtons/OfferSelectPackageButton'
import * as Analytics from 'analytics/analytics'
import { offerPageCheckAvailabilityEvent } from 'analytics/snowplow/events'
import { isMultiCartEnabled } from 'selectors/featuresSelectors'

interface Props {
  offer: App.Offer;
  packageView: OfferPackageOptionView;
  flightOrigin?: App.Airport;
  flightDestination?: App.Airport;
  rooms?: Array<App.Occupants>;
  checkIn: string;
  duration: number;
  fetching?: boolean;
  userMultiItemCartEnabled: boolean;
}

function LastMinutePackageOption(props: Props) {
  const {
    offer,
    flightOrigin,
    flightDestination,
    rooms,
    checkIn,
    duration,
    fetching,
    userMultiItemCartEnabled,
    packageView,
  } = props

  const {
    package: pkg,
    flightPrice,
  } = packageView

  const offerPageDispatch = useOfferPageDispatch()
  const [deals] = useFlightDeals('all', {
    originAirportCode: flightOrigin?.code,
    destinationAirportCode: flightDestination?.code,
  })

  const flightsEnabled = flightOrigin && offer.offerFlightsEnabled && (packageView.flightsAvailable || offer.bundledWithFlightsOnly) && config.businessTraveller.currentAccountMode !== 'business'
  const hasFlights = !!flightPrice && !offer.flightCacheDisabled

  const onToggleFilter = useCallback((filter: OfferPageFilterType) => {
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter })
  }, [offerPageDispatch, pkg.roomType])

  const handleChangeAirport = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.stopPropagation()
    onToggleFilter('airport')
  }, [onToggleFilter])

  const availableForPurchase = useIsAvailableForPurchase()
  const showPrices = packageView.capacityValid && packageView.hotelAvailable
  const canBuyNow = !!checkIn && !!rooms && packageView.fromCalendarInfo
  const canAddToCartNow = userMultiItemCartEnabled && canBuyNow
  const showIncludedGuestsExceeded = useMemo(() => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer), [pkg, rooms, offer])
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const departureTooEarly = useMemo(() => {
    const minDay = 1
    return !!checkInMoment && checkInMoment.diff(moment(), 'days') < minDay
  }, [checkInMoment])
  const flightsBookable = flightOrigin?.code !== flightDestination?.code && !departureTooEarly

  const checkOutMoment = useMemo(() => checkInMoment?.clone().add(duration, 'days'), [checkInMoment, duration])
  const { inclusions, bonusInclusions } = useOfferInclusions(pkg, { checkIn: checkInMoment, checkOut: checkOutMoment })
  const hasInclusions = bonusInclusions.length > 0 || inclusions.length > 0
  const showFlightsDealTag = flightsEnabled && flightOrigin?.code && flightDestination?.code && deals.length > 0

  const actionsState = useMemo<AccommodationRoomTileActionsState | undefined>(() => {
    if (!showPrices) {
      if (packageView.hotelAvailable && !packageView.capacityValid) return 'exceeded-capacity'
      if (!packageView.hotelAvailable && checkIn) return 'sold-out-on-dates'
      if (!packageView.hotelAvailable) return 'sold-out'
    }
  }, [checkIn, packageView.capacityValid, packageView.hotelAvailable, showPrices])

  const handleCheckAvailabilityClick = useCallback(() => {
    if (!canBuyNow) {
      Analytics.trackEvent(offerPageCheckAvailabilityEvent({ offer, roomRateId: pkg.roomRate?.id, includesFlights: hasFlights }))
    }
  }, [canBuyNow, offer, pkg.roomRate?.id, hasFlights])

  const handleChangeDatesClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.stopPropagation()
    onToggleFilter('checkin')
  }, [onToggleFilter])

  const handleAddAnotherUnitClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.stopPropagation()
    onToggleFilter('capacity')
  }, [onToggleFilter])

  if (!pkg.roomType) { return null }

  return (
    <Pane shadow="bottom-small" data-testid="package-option">
      <ScrollAnchor id={pkg.roomType.id} vOffset={rem(80)} />
      <AccommodationRoomTile roomPackage={pkg as App.HotelPackage}>
        <AccommodationRoomTileImage
          // @ts-expect-error not HotelPackage
          roomPackage={pkg}
        />
        <AccommodationRoomTileDetails
          // @ts-expect-error not HotelPackage
          roomPackage={pkg}
          actions={availableForPurchase && packageView.capacityValid && <CSSBreakpoint min="desktop">
            <TripGuard>
              <BookmarkButton offer={offer} roomTypeId={pkg.roomType.id} />
            </TripGuard>
          </CSSBreakpoint>}
        >
          {availableForPurchase && <AccommodationRoomTileActions
            // @ts-expect-error not HotelPackage
            roomPackage={pkg}
            state={actionsState}
            onAddAnotherUnitClick={handleAddAnotherUnitClick}
            onChangeDatesClick={handleChangeDatesClick}
          />}
        </AccommodationRoomTileDetails>
      </AccommodationRoomTile>
      <Divider kind="primary" />
      <AccommodationPackageTile>
        <AccommodationPackageTileContentArea>
          <AccommodationPackageTileDetails
            packageNumber={1}
            // @ts-expect-error not App.HotelPackage
            roomPackage={packageView.package}
          />
          <AccommodationPackageTileInclusions>
            <LargeContentToggle
              maxHeight={208}
              showMoreText="View more inclusions"
              showLessText="View less inclusions"
            >
              {hasInclusions && <InclusionsSection
                inclusions={inclusions}
                bonusInclusions={bonusInclusions}
              />}
            </LargeContentToggle>
          </AccommodationPackageTileInclusions>
        </AccommodationPackageTileContentArea>
        <AccommodationPackageTilePricingArea>
          {availableForPurchase && <WalledContent enforced={offer.walledGarden}>
            <AccommodationPackageTilePricingBlocks>
              {showPrices && <AccommodationPackageTilePricingBlock
                data-testid="pricing"
                title="Hotel"
                callToAction={<>
                  <OfferBookButton
                    packageView={packageView}
                    data-testid={cn('HotelBookingBtn', 'buy-now-button')}
                    disabled={fetching}
                    onSubmit={handleCheckAvailabilityClick}
                  >
                    {canBuyNow ? 'Book now' : 'Check availability'}
                  </OfferBookButton>
                  {canAddToCartNow && <OfferAddToCartButton disabled={fetching} packageView={packageView} />}
                </>
                }
              >
                {checkIn && <PriceRowPriceCaption>
                  {pluralizeToString('night', pkg.duration)}
                </PriceRowPriceCaption>}
                {!checkIn && <PriceRowPriceCaption>
                  {pluralizeToString('night', pkg.duration)} from
                </PriceRowPriceCaption>}
                <PriceRowPrice
                  size="M"
                  price={packageView.hotelPrice}
                  saleUnit="total"
                />
                <PriceRowTaxesAndFees />
                <VerticalSpacer gap={4}>
                  {!!pkg?.discountPercent && <PriceRowValueLastMinuteDiscount
                    discountPercentage={pkg.discountPercent}
                  />}
                  {showIncludedGuestsExceeded && <AdditionalGuestsPopup
                    complex={false}
                    description="+ Extra guest surcharge"
                    modalContent={pkg.roomPolicyDescription}
                  />}
                  {config.businessTraveller.currentAccountMode === 'business' && <BusinessTravellerOfferCreditsTextLink
                    type="exact"
                    offer={offer}
                    totalCost={packageView.hotelPrice}
                    numberOfNights={pkg.duration}
                    roomCount={rooms?.length}
                  />}
                </VerticalSpacer>
              </AccommodationPackageTilePricingBlock>}
              {showPrices && flightsEnabled && <AccommodationPackageTilePricingBlock
                data-testid="flights-pricing"
                title="Hotel + flights"
                callToAction={<>
                  {showFlightsDealTag && <Label variant="critical" kind="plain" startIcon={<SolidFireIcon />}>Flight deal available</Label>}
                  {flightsBookable && (!userMultiItemCartEnabled || !canBuyNow) && <OfferBookButton
                    packageView={packageView}
                    data-buytype="flights"
                    data-testid="HotelAndFlightsBtn"
                    disabled={fetching}
                  >
                    {canBuyNow ? 'Book now' : 'Check availability'}
                  </OfferBookButton>}
                  {flightsBookable && canAddToCartNow && <OfferSelectPackageButton
                    packageView={packageView}
                    data-buytype="flights"
                    disabled={fetching}
                  />}
                  {flightOrigin?.code !== flightDestination?.code && departureTooEarly && <TextButton
                    kind="tertiary"
                    onClick={handleChangeDatesClick}
                  >
                    Change departure date
                  </TextButton>}
                  {!hasFlights && flightOrigin?.code === flightDestination?.code && <TextButton
                    kind="tertiary"
                    onClick={handleChangeAirport}
                  >
                    Change airport
                  </TextButton>}
                </>}
              >
                {!hasFlights && flightOrigin?.code === flightDestination?.code && <PriceRowCaption>
                  No flights available from {flightOrigin?.name}. Please select another airport.
                </PriceRowCaption>}
                {flightOrigin?.code !== flightDestination?.code && departureTooEarly && <PriceRowCaption>
                  Flights cannot be booked online when check-in is within 24 hours.
                </PriceRowCaption>}
                {!hasFlights && flightsBookable && (
                  <PriceRowCaption>
                    Add flights to the hotel package. Select from all
                    major airlines.
                  </PriceRowCaption>
                )}
                {hasFlights && !departureTooEarly && <>
                  <PriceRowPriceCaption>
                    {pluralizeToString('night', pkg.duration)} from
                  </PriceRowPriceCaption>
                  <PriceRowPrice
                    size="M"
                    price={packageView.hotelPrice}
                    saleUnit="total*"
                  />
                  {!rooms && <PriceRowCaption>Two Adults</PriceRowCaption>}
                  <PriceRowTaxesAndFees />
                  {showIncludedGuestsExceeded && <AdditionalGuestsPopup
                    complex={false}
                    description="+ Extra guest surcharge"
                    modalContent={pkg.roomPolicyDescription}
                  />}
                  {config.businessTraveller.currentAccountMode === 'business' && <BusinessTravellerOfferCreditsTextLink
                    type="exact"
                    offer={offer}
                    totalCost={packageView.hotelPrice}
                    numberOfNights={pkg.duration}
                    roomCount={rooms?.length}
                  />}
                </>}
                <AtolProtectedTooltip />
              </AccommodationPackageTilePricingBlock>}
            </AccommodationPackageTilePricingBlocks>
            <LoadingIndicator visible={fetching} floating opaque />
          </WalledContent>}
        </AccommodationPackageTilePricingArea>
      </AccommodationPackageTile>
    </Pane>
  )
}

export default withOfferPageAndReduxState((state, appState, ownProps: Props) => ({
  rooms: state.rooms,
  flightOrigin: state.airport,
  checkIn: state.checkInDate,
  duration: state.duration,
  fetching: !!appState.calendar.calendarsLoading[ownProps.packageView.package.uniqueKey],
  userMultiItemCartEnabled: isMultiCartEnabled(appState),
}), LastMinutePackageOption)
